import type { ManiaChart, ManiaLane } from '../../maniaNote.js';
import type { ManiaReplayInputs, ReplayOutcome } from '../../replay.js';
import { computeAccuracy } from '../../scoring.js';
import {
  buildManiaEngineConfig,
  step,
  type ManiaEngineEvent,
  type ManiaEngineInput,
  type ManiaEngineTuning,
} from './core.js';
import {
  createManiaInitialState,
  resetManiaEngineStateForNextLoop,
} from './state.js';

export interface ManiaRunReplayOptions {
  stepBeats?: number;
  collectEvents?: boolean;
}

export interface ManiaRunReplayResult {
  outcome: ReplayOutcome;
  events: ManiaEngineEvent[];
  /** Number of full chart loops the player cleared. 0 if they died in loop 1. */
  loopsCompleted: number;
}

const DEFAULT_STEP_BEATS = 1 / 16;

/**
 * Replay a captured 4-lane mania run (Piano or Guitar) through the
 * deterministic engine.
 *
 * Walks `replay.loops` in order, simulating one chart traversal per
 * loop and resetting chart-relative engine state between loops via
 * {@link resetManiaEngineStateForNextLoop}. Mirror of the Circles
 * runner — see that doc-comment for the multi-loop rationale.
 */
export function runManiaReplay(
  chart: ManiaChart,
  replay: ManiaReplayInputs,
  tuning: ManiaEngineTuning,
  opts: ManiaRunReplayOptions = {},
): ManiaRunReplayResult {
  const config = buildManiaEngineConfig(chart, tuning);
  const state = createManiaInitialState(chart);
  const stepBeats = opts.stepBeats ?? DEFAULT_STEP_BEATS;
  const collectEvents = opts.collectEvents !== false;
  const allEvents: ManiaEngineEvent[] = [];

  let loopsCompleted = 0;
  for (let loopIdx = 0; loopIdx < replay.loops.length; loopIdx += 1) {
    if (loopIdx > 0) {
      resetManiaEngineStateForNextLoop(state);
    }
    const inputs: ManiaEngineInput[] = replay.loops[loopIdx].inputs.map((i) => ({
      beat: i.beat,
      lane: i.lane,
      type: i.type,
      // Pre-source replays default to 'keyboard' — the symmetric judging
      // they were recorded against most closely matches today's strict
      // keyboard early window, so re-simulation drifts the least.
      source: i.source ?? 'keyboard',
    }));
    let cursor = 0;
    let nextInputIdx = 0;

    while (cursor < chart.totalBeats && state.alive && !state.completed) {
      const tickEnd = Math.min(cursor + stepBeats, chart.totalBeats);
      const tickInputs: ManiaEngineInput[] = [];
      while (
        nextInputIdx < inputs.length &&
        inputs[nextInputIdx].beat <= tickEnd
      ) {
        tickInputs.push(inputs[nextInputIdx]);
        nextInputIdx += 1;
      }
      const out = step({
        config,
        state,
        fromBeat: cursor,
        toBeat: tickEnd,
        inputs: tickInputs,
      });
      if (collectEvents) {
        allEvents.push(...out.events);
      }
      cursor = tickEnd;
    }

    if (state.alive && !state.completed && cursor < chart.totalBeats) {
      const out = step({
        config,
        state,
        fromBeat: cursor,
        toBeat: chart.totalBeats,
        inputs: [],
      });
      if (collectEvents) {
        allEvents.push(...out.events);
      }
    }

    if (state.completed) {
      loopsCompleted += 1;
    }
    if (!state.alive) {
      break;
    }
  }

  const outcome: ReplayOutcome = {
    completed: loopsCompleted > 0,
    score: state.score,
    combo: state.combo,
    maxCombo: state.maxCombo,
    perfects: state.perfects,
    goods: state.goods,
    misses: state.misses,
    accuracy: computeAccuracy(state.perfects, state.goods, 0, state.misses),
    deathBeat: state.deathBeat,
    deathReason: state.deathReason ?? undefined,
  };

  return { outcome, events: allEvents, loopsCompleted };
}

/**
 * Build the deterministic "perfect" replay for a 4-lane mania chart:
 * a `down` press exactly on every lane event's beat, plus a matching
 * `up` release at `holdEndBeat` for every hold note. Dual tiles
 * produce two presses + two releases (one per lane) at the same
 * beats. Used by builtin-map smoke tests (every chart must complete
 * with this payload) and the future authoring/editor preview's "play
 * perfect" UI.
 *
 * Ordering: events sort by `(beat asc, type-down-before-up,
 * lane asc)`. The down-before-up tiebreak matches the engine's
 * sort in `step()` — a same-beat down+up pair resolves head before
 * release. Lane-asc on the final tier is purely cosmetic / stable.
 */
export function buildPerfectManiaReplayInputs(chart: ManiaChart): Array<{
  beat: number;
  lane: ManiaLane;
  type: 'down' | 'up';
}> {
  const out: Array<{ beat: number; lane: ManiaLane; type: 'down' | 'up' }> = [];
  for (const note of chart.notes) {
    out.push({ beat: note.beat, lane: note.lane, type: 'down' });
    if (note.dualPartnerLane !== undefined) {
      out.push({ beat: note.beat, lane: note.dualPartnerLane, type: 'down' });
    }
    if (note.holdEndBeat !== undefined) {
      out.push({ beat: note.holdEndBeat, lane: note.lane, type: 'up' });
      if (note.dualPartnerLane !== undefined) {
        out.push({ beat: note.holdEndBeat, lane: note.dualPartnerLane, type: 'up' });
      }
    }
  }
  return out.sort((a, b) => {
    if (a.beat !== b.beat) {
      return a.beat - b.beat;
    }
    if (a.type !== b.type) {
      return a.type === 'down' ? -1 : 1;
    }
    return a.lane - b.lane;
  });
}
